/**
 * Tab-order helpers shared by the focus-trapping surfaces (`<Modal>` and the
 * portalled editor popovers). A selector alone over-reports: `[tabindex]` also
 * matches `-2`, and the matched element may sit under `display: none`, an
 * `inert` or `aria-hidden` subtree, none of which the browser will ever focus.
 * Trapping on the raw selector made the "last" element a node Tab can't reach,
 * so the wrap never fired and focus walked out of the dialog.
 */

export const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[contenteditable="true"]',
  '[contenteditable=""]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function isRendered(el: HTMLElement): boolean {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true })
  }
  // jsdom and older engines: walk up and read computed display / visibility.
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    const style = getComputedStyle(node)
    if (style.display === 'none') return false
    if (node === el && style.visibility === 'hidden') return false
  }
  return true
}

export function isTabbable(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled')) return false
  const tabindex = el.getAttribute('tabindex')
  if (tabindex !== null && Number(tabindex) < 0) return false
  if (el.closest('[inert], [aria-hidden="true"]')) return false
  return isRendered(el)
}

/** Elements inside `root` that Tab can actually land on, in DOM order. */
export function getTabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(isTabbable)
}
